import { useState, useCallback } from 'react';
import { OnboardingFormData, OnboardingStep } from '../types';
import { CustomerType, IndividualCustomerOnboardingDTO, GroupCustomerOnboardingDTO } from "@/shared/api/model";
import { createIndividualCustomer as createIndividualCustomerAPI, createGroupCustomer as createGroupCustomerAPI } from '@/shared/api/client/customer-onboarding-controller';

interface CustomerCreationResult {
  success: boolean;
  message: string;
  customerId?: number;
  errors?: Record<string, string>;
}

interface OrganizedPayload {
  categoryId: number;
  steps: Record<string, any>;
  summary: {
    totalSteps: number;
    completedSteps: number;
    fieldGroups: string[];
    hasProducts: boolean;
    hasLocations: boolean;
    hasDocuments: boolean;
    hasBiometrics: boolean;
  };
}

const useCustomerCreation = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<CustomerCreationResult | null>(null);

  // Helper function to convert string to camelCase
  const toCamelCase = (str: string): string => {
    return str
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
      })
      .replace(/\s+/g, '')
      .replace(/[^\w]/g, '');
  };

  const createIndividualCustomer = useCallback(async (
    categoryId: number,
    formData: OnboardingFormData,
    steps?: OnboardingStep[]
  ): Promise<CustomerCreationResult> => {
    setIsSubmitting(true);
    
    try {
      // Organize data by step names (in camelCase)
      const organizedSteps: Record<string, any> = {};

      // Process field group steps
      if (steps) {
        steps.forEach(step => {
          if (step.component === 'FieldGroup' && step.fieldGroup) {
            const stepData: Record<string, any> = {};
            
            step.fieldGroup.fields?.forEach(field => {
              if (!field.fieldName) return;
              const value = formData.customerData[field.fieldName];
              if (value !== undefined && value !== null && value !== '') {
                stepData[field.fieldName] = value;
              }
            });

            if (Object.keys(stepData).length > 0) {
              const stepKey = toCamelCase(step.title);
              organizedSteps[stepKey] = {
                stepId: step.id,
                stepTitle: step.title,
                groupKey: step.fieldGroup.groupKey,
                data: stepData,
                isCompleted: step.isCompleted,
              };
            }
          }
        });
      }

      // Add products step
      if (formData.productData && formData.productData.length > 0) {
        organizedSteps['productSelection'] = {
          stepId: 'products',
          stepTitle: 'Product Selection',
          data: formData.productData.map(product => ({
            productId: product.id,
              glNo: product.glNo,
            productCode: product.glNo,
            productName: product.productName,
            productType: product.productType,
          })),
          count: formData.productData.length,
        };
      }

      // Add locations step
      if (formData.locationData && formData.locationData.length > 0) {
        organizedSteps['addressInformation'] = {
          stepId: 'locations',
          stepTitle: 'Address Information',
          data: formData.locationData.map(location => ({
            locationConfigName: location.locationConfigName,
            addressType: location.addressType || 'PHYSICAL',
            locationSelections: location.locationSelections,
            houseNumber: location.houseNumber,
            postalCode: location.postalCode,
            gpsCoordinates: location.gpsCoordinates,
            isPrimary: location.isPrimary || false,
          })),
          count: formData.locationData.length,
        };
      }

      // Add documents step
      if (formData.documentData && formData.documentData.length > 0) {
        organizedSteps['documentUpload'] = {
          stepId: 'documents',
          stepTitle: 'Document Upload',
          data: formData.documentData.map(document => ({
            documentTypeCode: document.documentTypeCode,
            documentTypeName: document.documentTypeName,
            fileId: document.fileId,
            fileName: document.fileName,
            isRequired: document.isRequired,
          })),
          count: formData.documentData.length,
        };
      }

      // Prepare organized payload
      const organizedPayload: OrganizedPayload = {
        categoryId,
        steps: organizedSteps,
        summary: {
          totalSteps: Object.keys(organizedSteps).length,
          completedSteps: steps?.filter(s => s.isCompleted).length || 0,
          fieldGroups: steps?.filter(s => s.component === 'FieldGroup').map(s => s.title) || [],
          hasProducts: !!formData.productData && formData.productData.length > 0,
          hasLocations: !!formData.locationData && formData.locationData.length > 0,
          hasDocuments: !!formData.documentData && formData.documentData.length > 0,
          hasBiometrics: !!(formData.photoFileId || formData.signatureFileId),
        },
      };

      // Legacy flat payload for backward compatibility
      const legacyPayload = {
        categoryId,
        customerData: formData.customerData,
        custInfoData: {
          ...formData.custInfoData,
          photoFileId: formData.photoFileId,
          signatureFileId: formData.signatureFileId,
          photoUploadedAt: formData.photoUploadedAt,
          signatureUploadedAt: formData.signatureUploadedAt,
        },
        products: formData.productData || [],
        locations: formData.locationData || [],
        documents: formData.documentData || [],
      };

      // Prepare DTO for backend API
      const apiPayload: IndividualCustomerOnboardingDTO = {
        categoryId,
        customerData: formData.customerData,
        custInfoData: formData.custInfoData || {},
        locationData: formData.locationData?.map(location => ({
          addressType: location.addressType || 'HOME',
          zone: location.locationSelections?.zone || undefined,
          region: location.locationSelections?.region || undefined,
          district: location.locationSelections?.district || undefined,
          ward: location.locationSelections?.ward || undefined,
          street: location.locationSelections?.street || undefined,
          houseNumber: location.houseNumber || undefined,
          postalCode: location.postalCode || undefined,
          latitude: undefined,
          longitude: undefined,
          gpsAccuracy: undefined,
          isPrimary: location.isPrimary || false,
          displayOrder: location.displayOrder || 0,
          zoneId: location.locationSelections?.zone?.id || undefined,
          regionId: location.locationSelections?.region?.id || undefined,
          districtId: location.locationSelections?.district?.id || undefined,
          wardId: location.locationSelections?.ward?.id || undefined,
          streetId: location.locationSelections?.street?.id || undefined,
        })) || [],
        documentData: formData.documentData?.map(document => ({
          documentType: document.documentTypeCode || '',
          documentTypeName: document.documentTypeName || '',
          fileId: document.fileId || '',
          fileName: document.fileName || '',
          fileUrl: undefined,
          fileSize: undefined,
          mimeType: undefined,
          uploadedAt: document.uploadedAt ? new Date(document.uploadedAt).toISOString() : undefined,
          uploadedBy: undefined,
          isRequired: document.isRequired || false,
          notes: undefined,
        })) || [],
        productData: formData.productData?.map(product => ({
          glNo: product.glNo || product.productCode || '',
          productName: product.productName || '',
          productType: product.productType || '',
          productTypeName: undefined,
          initialDeposit: undefined,
          loanAmount: undefined,
          loanTerm: undefined,
          loanPurpose: undefined,
          isDefault: product.isDefault || false,
          displayOrder: product.displayOrder || 0,
          additionalData: undefined,
        })) || [],
        photoFileId: formData.photoFileId,
        signatureFileId: formData.signatureFileId,
        photoUploadedAt: formData.photoUploadedAt,
        signatureUploadedAt: formData.signatureUploadedAt,
        submittedBy: undefined,
        submittedAt: undefined,
        branchCode: undefined,
        remarks: undefined,
      };

      console.log('=== INDIVIDUAL CUSTOMER SUBMISSION ===');
      console.log('API Payload:', JSON.stringify(apiPayload, null, 2));
      console.log('======================================');

      // Call backend API
      const response = await createIndividualCustomerAPI(apiPayload);

      const result: CustomerCreationResult = {
        success: response.success || false,
        message: response.message || 'Individual customer created successfully',
        customerId: response.customerId || undefined,
      };

      setLastResult(result);
      return result;

    } catch (error) {
      const result: CustomerCreationResult = {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create individual customer',
        errors: { general: 'Creation failed' },
      };

      setLastResult(result);
      return result;

    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const createGroupCustomer = useCallback(async (
    categoryId: number,
    formData: OnboardingFormData
  ): Promise<CustomerCreationResult> => {
    setIsSubmitting(true);
    
    try {
      // Prepare complete submission payload
      const submissionPayload = {
        categoryId,
        groupData: formData.customerData,
        members: formData.groupMembers || [],
        products: formData.productData?.map(product => ({
          productId: product.productId,
          productCode: product.productCode,
          productName: product.productName,
          productType: product.productType,
        })) || [],
        locations: formData.locationData?.map(location => ({
          locationConfigName: location.locationConfigName,
          addressType: location.addressType || 'PHYSICAL',
          locationSelections: location.locationSelections,
          houseNumber: location.houseNumber,
          postalCode: location.postalCode,
          gpsCoordinates: location.gpsCoordinates,
          isPrimary: location.isPrimary || false,
        })) || [],
        documents: formData.documentData?.map(document => ({
          documentTypeCode: document.documentTypeCode,
          documentTypeName: document.documentTypeName,
          fileId: document.fileId,
          fileName: document.fileName,
          isRequired: document.isRequired,
        })) || [],
      };

      // Get member template ID from category (assuming it's stored in formData or passed separately)
      // For now, we'll use a placeholder - this should come from the category configuration
      const memberTemplateId = formData.customerData.memberTemplateId as number | undefined;

      // Prepare DTO for backend API
      const apiPayload: GroupCustomerOnboardingDTO = {
        categoryId,
        memberTemplateId,
        customerData: formData.customerData,
        custInfoData: formData.custInfoData || {},
        locationData: formData.locationData?.map(location => ({
          addressType: location.addressType || 'HOME',
          zone: location.locationSelections?.zone || undefined,
          region: location.locationSelections?.region || undefined,
          district: location.locationSelections?.district || undefined,
          ward: location.locationSelections?.ward || undefined,
          street: location.locationSelections?.street || undefined,
          houseNumber: location.houseNumber || undefined,
          postalCode: location.postalCode || undefined,
          latitude: undefined,
          longitude: undefined,
          gpsAccuracy: undefined,
          isPrimary: location.isPrimary || false,
          displayOrder: location.displayOrder || 0,
          zoneId: location.locationSelections?.zone?.id || undefined,
          regionId: location.locationSelections?.region?.id || undefined,
          districtId: location.locationSelections?.district?.id || undefined,
          wardId: location.locationSelections?.ward?.id || undefined,
          streetId: location.locationSelections?.street?.id || undefined,
        })) || [],
        documentData: formData.documentData?.map(document => ({
          documentType: document.documentTypeCode || '',
          documentTypeName: document.documentTypeName || '',
          fileId: document.fileId || '',
          fileName: document.fileName || '',
          fileUrl: undefined,
          fileSize: undefined,
          mimeType: undefined,
          uploadedAt: document.uploadedAt ? new Date(document.uploadedAt).toISOString() : undefined,
          uploadedBy: undefined,
          isRequired: document.isRequired || false,
          notes: undefined,
        })) || [],
        productData: formData.productData?.map(product => ({
          glNo: product.glNo || product.productCode || '',
          productName: product.productName || '',
          productType: product.productType || '',
          productTypeName: undefined,
          initialDeposit: undefined,
          loanAmount: undefined,
          loanTerm: undefined,
          loanPurpose: undefined,
          isDefault: product.isDefault || false,
          displayOrder: product.displayOrder || 0,
          additionalData: undefined,
        })) || [],
        groupMembers: formData.groupMembers?.map(member => ({
          id: member.id,
          isLeader: member.isLeader,
          role: member.role,
          data: member.data || {},
          products: member.products?.map(product => ({
            glNo: product.glNo || product.productCode || '',
            productName: product.productName || '',
            productType: product.productType || '',
            productTypeName: product.productTypeName,
            initialDeposit: product.initialDeposit,
            loanAmount: product.loanAmount,
            loanTerm: product.loanTerm,
            loanPurpose: product.loanPurpose,
            isDefault: product.isDefault || false,
            displayOrder: product.displayOrder || 0,
            additionalData: product.additionalData,
          })) || [],
          locations: member.locations?.map(location => ({
            addressType: location.addressType || 'HOME',
            zone: location.zone || undefined,
            region: location.region || undefined,
            district: location.district || undefined,
            ward: location.ward || undefined,
            street: location.street || undefined,
            houseNumber: location.houseNumber || undefined,
            postalCode: location.postalCode || undefined,
            latitude: undefined,
            longitude: undefined,
            gpsAccuracy: undefined,
            isPrimary: location.isPrimary || false,
            displayOrder: location.displayOrder || 0,
            zoneId: location.zoneId || undefined,
            regionId: location.regionId || undefined,
            districtId: location.districtId || undefined,
            wardId: location.wardId || undefined,
            streetId: location.streetId || undefined,
          })) || [],
          documents: member.documents?.map(document => ({
            documentType: document.documentType || '',
            documentTypeName: document.documentTypeName || '',
            fileId: document.fileId || '',
            fileName: document.fileName || '',
            fileUrl: document.fileUrl,
            fileSize: document.fileSize,
            mimeType: document.mimeType,
            uploadedAt: document.uploadedAt ? new Date(document.uploadedAt).toISOString() : undefined,
            uploadedBy: document.uploadedBy,
            isRequired: document.isRequired || false,
            notes: document.notes,
          })) || [],
          customerData: member.customerData,
          custInfoData: member.custInfoData,
        })) || [],
        photoFileId: formData.photoFileId,
        signatureFileId: formData.signatureFileId,
        photoUploadedAt: formData.photoUploadedAt,
        signatureUploadedAt: formData.signatureUploadedAt,
        submittedBy: undefined,
        submittedAt: undefined,
        branchCode: undefined,
        remarks: undefined,
      };

      console.log('=== GROUP CUSTOMER SUBMISSION ===');
      console.log('API Payload:', JSON.stringify(apiPayload, null, 2));
      console.log('=================================');

      // Call backend API
      const response = await createGroupCustomerAPI(apiPayload);

      const result: CustomerCreationResult = {
        success: response.success || false,
        message: response.message || 'Group customer created successfully',
        customerId: response.customerId || undefined,
      };

      setLastResult(result);
      return result;

    } catch (error) {
      const result: CustomerCreationResult = {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create group customer',
        errors: { general: 'Creation failed' },
      };

      setLastResult(result);
      return result;

    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const createBusinessCustomer = useCallback(async (
    categoryId: number,
    formData: OnboardingFormData
  ): Promise<CustomerCreationResult> => {
    setIsSubmitting(true);
    
    try {
      // Placeholder for business customer creation
      console.log('Creating business customer:', {
        categoryId,
        businessData: formData.customerData,
        products: formData.productData,
        locations: formData.locationData,
        documents: formData.documentData,
      });

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 3000));

      const result: CustomerCreationResult = {
        success: true,
        message: 'Business customer created successfully',
        customerId: Math.floor(Math.random() * 10000), // Mock ID
      };

      setLastResult(result);
      return result;

    } catch (error) {
      const result: CustomerCreationResult = {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create business customer',
        errors: { general: 'Creation failed' },
      };

      setLastResult(result);
      return result;

    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const createCustomer = useCallback(async (
    customerType: CustomerType,
    categoryId: number,
    formData: OnboardingFormData
  ): Promise<CustomerCreationResult> => {
    switch (customerType) {
      case CustomerType.INDIVIDUAL:
        return createIndividualCustomer(categoryId, formData);
      case CustomerType.GROUP:
        return createGroupCustomer(categoryId, formData);
      case CustomerType.BUSINESS:
        return createBusinessCustomer(categoryId, formData);
      default:
        throw new Error(`Unsupported customer type: ${customerType}`);
    }
  }, [createIndividualCustomer, createGroupCustomer, createBusinessCustomer]);

  const validateFormData = useCallback((
    customerType: CustomerType,
    formData: OnboardingFormData
  ): { isValid: boolean; errors: Record<string, string> } => {
    const errors: Record<string, string> = {};

    // Common validations
    if (!formData.customerData || Object.keys(formData.customerData).length === 0) {
      errors.customerData = 'Customer information is required';
    }

    if (!formData.productData || formData.productData.length === 0) {
      errors.products = 'At least one product must be selected';
    }

    // Validate required documents are uploaded
    if (formData.documentData) {
      const requiredDocs = formData.documentData.filter(doc => doc.isRequired);
      const uploadedRequiredDocs = requiredDocs.filter(doc => doc.fileId);
      
      if (requiredDocs.length !== uploadedRequiredDocs.length) {
        errors.documents = 'All required documents must be uploaded';
      }
    }

    // Validate required locations are filled
    if (formData.locationData) {
      const hasInvalidLocations = formData.locationData.some(location => 
        !location.locationSelections || Object.keys(location.locationSelections).length === 0
      );
      
      if (hasInvalidLocations) {
        errors.locations = 'All location requirements must be completed';
      }
    }

    // Type-specific validations
    switch (customerType) {
      case CustomerType.GROUP:
        if (!formData.groupMembers || formData.groupMembers.length === 0) {
          errors.groupMembers = 'At least one group member is required';
        } else {
          const hasLeader = formData.groupMembers.some(member => member.isLeader);
          if (!hasLeader) {
            errors.groupMembers = 'At least one group member must be designated as leader';
          }
        }
        break;

      case CustomerType.BUSINESS:
        // Add business-specific validations here
        break;
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }, []);

  const clearResult = useCallback(() => {
    setLastResult(null);
  }, []);

  return {
    // State
    isSubmitting,
    lastResult,

    // Actions
    createCustomer,
    createIndividualCustomer,
    createGroupCustomer,
    createBusinessCustomer,
    validateFormData,
    clearResult,
  };
};

export default useCustomerCreation;
